import { AuthService } from '@auth0/auth0-angular';
import { CanActivateFn } from '@angular/router';
import { inject } from '@angular/core';

import { map, switchMap, of, Observable } from 'rxjs';

import { PharmasyncService } from '../components/pharma-sync/service/pharmasync.service';

import { PermissionService } from '../services/common/permission.service'
  ;
import { PERMISSIONS_GROUPS } from '../constants/permission-group.constants';
import { OnboardService } from '../services/common/onboard.service';

/**
 * Verifica se o usuario tem permissao para acessar a rota.
 * Caso o usuario seja freemium, verifica se o PharmaSynch esta ativo.
 * Caso contrario, verifica se o usuario tem a permissao necessária.
 * @param route Rota que esta sendo acessada.
 * @param state Estado da rota.
 * @returns Um Observable que emite um booleano.
 */
export const permissionGuard: CanActivateFn = (route, state) => {
  const permissionService: PermissionService = inject(PermissionService);
  const pharmaSyncService: PharmasyncService = inject(PharmasyncService);
  const onboardService: OnboardService = inject(OnboardService)
  const auth0: AuthService = inject(AuthService);

  const FREEMIUM_GROUPS: string[] = [PERMISSIONS_GROUPS.freemium, PERMISSIONS_GROUPS.otimiza];

  const requiredGroup = route.data["requiredGroup"];
  const currentUrl = state.url;

  return auth0.idTokenClaims$.pipe(
    switchMap(response => {
      const groups = response?.["https://rmsaas.co/groups"] || [];
      const hasFreemiumGroup = groups.some((g: string) => FREEMIUM_GROUPS.includes(g));
      // Se não for usuário freemium, usa o fluxo padrão de verificação de permissão
      if (!hasFreemiumGroup) {
        return checkStandardPermission(permissionService, requiredGroup);
      }
      // Fluxo para usuários freemium com PharmaSynch
      return handleFreemiumUser(pharmaSyncService, permissionService, requiredGroup, onboardService, currentUrl);
    })
  );
};

/**
 * Verifica se o usuario tem a permissao necessaria para acessar a rota.
 * Carrega as permiss oes do usuario e verifica se o grupo de permiss o
 * especificado est  presente na lista de permiss oes do usuario.
 * @param permissionService Servi o que gerencia as permiss oes do usuario.
 * @param requiredGroup Grupo de permiss o necess ario para acessar a rota.
 * @returns Um Observable que emite um booleano.
 */
function checkStandardPermission(
  permissionService: PermissionService,
  requiredGroup: string[]
): Observable<boolean> {
  return permissionService.loadUserGroups().pipe(
    map(() => permissionService.isInGroup(requiredGroup))
  );
}

/**
 * Função que verifica se o usuário é freemium e se o PharmaSynch
 * está ativo. Caso o status seja nulo, busca as permissões do usuário
 * e verifica se o PharmaSynch está ativo. Caso contrário, verifica se
 * o PharmaSynch está ativo e se o usuário tem a permissão necessária.
 * @param pharmaSyncService Serviço que gerencia o status do PharmaSynch.
 * @param permissionService Serviço que gerencia as permissões do usuário.
 * @param requiredGroup Grupo de permissão necessária para acessar a rota.
 * @returns Um Observable que emite um booleano.
 */
function handleFreemiumUser(
  pharmaSyncService: PharmasyncService,
  permissionService: PermissionService,
  requiredGroup: string[],
  onboardService: OnboardService,
  currentUrl: string
): Observable<boolean> {
  return pharmaSyncService.pharmasyncStatus$.pipe(
    switchMap(pharmasyncStatus => {
      if (pharmasyncStatus === null) {
        return fetchPermissionAndCheckStatus(pharmaSyncService, permissionService,onboardService,currentUrl, requiredGroup);
      }


      return checkPharmasyncStatus(pharmasyncStatus, pharmaSyncService, permissionService, onboardService ,requiredGroup, currentUrl);
    })
  );
}

/**
 * Carrega as permissoes do usuario e verifica se o PharmaSynch
 * esta ativo. Caso o status seja nulo, busca as permissoes do
 * usuario e verifica se o PharmaSynch esta ativo. Caso contrario,
 * verifica se o PharmaSynch esta  ativo e se o usuario tem a
 * permissao necessaario.
 * @param pharmaSyncService Servi o que gerencia o status do PharmaSynch.
 * @param permissionService Servi o que gerencia as permiss oes do usuario.
 * @param requiredGroup Grupo de permiss o necess ario para acessar a rota.
 * @returns Um Observable que emite um booleano.
 */
function fetchPermissionAndCheckStatus(
  pharmaSyncService: PharmasyncService,
  permissionService: PermissionService,
  onboardService: OnboardService,
  currentUrl: string,
  requiredGroup: string[]
): Observable<boolean> {
  return pharmaSyncService.fetchUserPermission().pipe(
    switchMap(() => pharmaSyncService.pharmasyncStatus$),
    switchMap(updatedStatus =>
      checkPharmasyncStatus(updatedStatus, pharmaSyncService, permissionService,onboardService, requiredGroup, currentUrl)
    )
  );
}

/**
 * Verifica se o PharmaSynch está ativo e se o usuário tem a permissão
 * necessária. Caso o status seja nulo, busca as permissões do usuário
 * e verifica se o PharmaSynch está ativo. Caso contrário, verifica se
 * o PharmaSynch está ativo e se o usuário tem a permissão necessária.
 * @param isPharmasyncActive Status do PharmaSynch.
 * @param pharmaSyncService Serviço que gerencia o status do PharmaSynch.
 * @param permissionService Serviço que gerencia as permissões do usuário.
 * @param requiredGroup Grupo de permissão necessária para acessar a rota.
 * @returns Um Observable que emite um booleano.
 */
function checkPharmasyncStatus(
  isPharmasyncActive: boolean | null,
  pharmaSyncService: PharmasyncService,
  permissionService: PermissionService,
  onboardService: OnboardService,
  requiredGroup: string[],
  currentUrl?: string
): Observable<boolean> {
  if (isPharmasyncActive) {
    // Verifica se é a rota de análise de compra e se o certificado A1 está presente
    if (currentUrl?.includes('dashboard/analise-de-compra')) {
      if (onboardService.hasCertificadoA1()){
        return of(true);
      }
      return of(false);
    }
    return of(true);
  }

  return permissionService.loadUserGroups().pipe(
    map(() => {
      const hasPermission = permissionService.isInGroup(requiredGroup);

      return hasPermission;
    })
  );
}
